import { Component, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { EmployeeService } from "src/app/services/employee.service";
import { Organization, ResetPasswordModel, ServiceRequestResponseTypes, UserClient, UserModel, UserProfession, UserProfessionClient, UserRoleTypes, WorkingGroupClient, WorkingGroups } from "src/app/web-api-client";
import { NomenclatrueService } from "../../../services/nomenclature.service";
import { User } from "../users/users.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, forkJoin } from "rxjs";
import { ServiceRequestResponseTypeUtil } from "../../../shared/utils/service-request-response.utils";
import Swal from "sweetalert2";

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent implements OnInit {

  public onClose = new Subject<boolean>();
  public isLoaded = false;
  public editUser: User;
  public organizations: Organization[];
  public workingGroups: WorkingGroups[] = [];
  public userProfessions: UserProfession[] = [];
  public UserRoleTypes = UserRoleTypes;
  public roleTypes = Object.keys(UserRoleTypes).filter(
    key => !isNaN(Number(UserRoleTypes[key]))
  );

  public responseTypes = Object.keys(ServiceRequestResponseTypes)
    .filter(key => !isNaN(Number(ServiceRequestResponseTypes[key])))
    .map(key => ({ name: key, value: ServiceRequestResponseTypes[key] }));

  userForm: FormGroup;
  passwordForm: FormGroup;

  // Password visibility toggles
  showNewPassword = false;
  showConfirmPassword = false;

  // Password change state
  isChangingPassword = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    public userClient: UserClient,
    public employeeService: EmployeeService,
    public nomenclatureService: NomenclatrueService,
    public workingGroupClient: WorkingGroupClient,
    public userProfessionClient: UserProfessionClient
  ) { }

  ngOnInit(): void {

    forkJoin([this.nomenclatureService.getOrganizations(), this.workingGroupClient.getList(), this.userProfessionClient.getList()]).subscribe(([orgs, workingGroups, userProfessions]) => {
      this.workingGroups = workingGroups;
      this.organizations = orgs;
      this.userProfessions = userProfessions;
      this.initializeForm();
      this.isLoaded = true;
    });


  }

  initializeForm() {
    this.userForm = this.fb.group({
      name: [this.editUser.firstName, Validators.required],
      surname: [this.editUser.lastName, Validators.required],
      username: [this.editUser.email, Validators.required],
      organization: [this.editUser.organizationId, Validators.required],
      workingGroup: [this.editUser.idWorkingGroup],
      userProfession: [this.editUser.userProfessionIdUserProfession],
      egn: [this.editUser.egn, Validators.required],
      isService: [this.editUser.isService, Validators.required],
      role: [this.editUser.role, Validators.required],
      responseType: [0]
    });

    // Initialize password reset form (admin resets user password - no current password needed)
    this.passwordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  // Custom validator to check if passwords match
  passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  };

  // Password visibility toggles
  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Getter for easy access to password form controls
  get pf() {
    return this.passwordForm.controls;
  }

  // Admin password reset submission
  onResetPassword(): void {
    if (this.passwordForm.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.passwordForm.controls).forEach(key => {
        this.passwordForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isChangingPassword = true;

    const model = new ResetPasswordModel();
    model.userId = this.editUser.id;
    model.newPassword = this.passwordForm.value.newPassword;

    this.userClient.resetPassword(model).subscribe({
      next: () => {
        this.isChangingPassword = false;
        this.passwordForm.reset();
        Swal.fire({
          title: 'Успех!',
          text: 'Паролата е променена успешно.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      },
      error: (error) => {
        this.isChangingPassword = false;
        let errorMessage = 'Възникна грешка при промяна на паролата.';
        if (error.status === 400) {
          errorMessage = 'Новата парола не отговаря на изискванията.';
        } else if (error.status === 403) {
          errorMessage = 'Нямате права за тази операция.';
        } else if (error.status === 404) {
          errorMessage = 'Потребителят не е намерен.';
        }
        Swal.fire({
          title: 'Грешка',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    });
  }

  closeModal() {
    this.onClose.next(true);
    this.activeModal.close();
  }

  onSubmit() {
    if (this.userForm.valid) {

      let editedUSer = new UserModel();

      editedUSer.organization = '',
        editedUSer.firstName = this.userForm.value.name,
        editedUSer.lastName = this.userForm.value.surname,
        editedUSer.role = this.userForm.value.role,
        editedUSer.organizationId = this.userForm.value.organization,
        editedUSer.idWorkingGroup = this.userForm.value.workingGroup,
        editedUSer.egn = this.userForm.value.egn;
      editedUSer.isService = this.userForm.value.isService;
      editedUSer.id = this.editUser.id;
      editedUSer.userProfessionIdUserProfession = this.userForm.value.userProfession;
      editedUSer.notificationType = +this.userForm.value.responseType;


      this.userClient.updateUser(editedUSer).subscribe((user) => {
        this.onClose.next(true);
        this.activeModal.close();
      });
    }
  }

    public GetCyrilicRequestType(requestType: ServiceRequestResponseTypes): string {

    return ServiceRequestResponseTypeUtil.getCyrilicLabel(requestType);
  }

}
